import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminAuth from '../components/admin/AdminAuth';
import ActionBar from '../components/admin/ActionBar';
import EngineSection from '../components/admin/EngineSection';
import WidgetPipelineBar from '../components/admin/WidgetPipelineBar';
import DailyProgress from '../components/admin/DailyProgress';
import { faXTwitter, faInstagram, faYoutube, faTiktok, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faBlog, faGlobe, faCalendarCheck, faMagnifyingGlass, faCalendarDays, faCircleCheck, faEye, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getDailySummaryStats,
  getFollowUpQueue,
  getBookingStats,
  getCarouselQueueCount,
  getUnreviewedCarouselCount,
  getCarouselCountsByTypeAndAccount,
  getChessWebSocialStats,
  getChessWebSocialQueueCount,
  getVideoQueueStats,
  getDailyActions,
  updateDailyAction,
  getAutoRejectedCarouselCount
} from '../services/contentOpsService';
import { getSubmissionStats } from '../services/submissionService';

// Returns human-readable label for next occurrence of given weekdays (0=Sun, 6=Sat)
const getNextDay = (days) => {
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().getDay();
  let minDist = 7;
  let nextIdx = days[0];
  for (const d of days) {
    const dist = (d - today + 7) % 7 || 7;
    if (dist < minDist) { minDist = dist; nextIdx = d; }
  }
  return names[nextIdx];
};

const SCHEDULES = {
  tweetPostTC:     { days: [0], label: (d) => d, tooltip: 'Post tweets @chesstrending — Sunday ~2 PM' },
  quotePostTC:     { days: [3], label: (d) => d, tooltip: 'Post quotes @chesstrending — Wednesday ~8 PM' },
  tweetPostIC:     { days: [3], label: (d) => d, tooltip: 'Post tweets @IndieChess — Wednesday ~9 PM' },
  quotePostIC:     { days: [0], label: (d) => d, tooltip: 'Post quotes @IndieChess — Sunday ~3 PM' },
  tweetFetch:      { days: [1, 2, 4, 6], label: () => 'Mon · Tue · Thu · Sat', tooltip: 'Tweet fetch — Desktop + Cloud Fn' },
  quoteFetch:      { days: [3], label: () => 'Wed', tooltip: '/quote-research — Desktop, 12:06 PM' },
  widgetDiscovery: { days: [0,1,2,3,4,5,6], label: () => 'Daily 4 PM', tooltip: '/widget-spotlight + /widget-tweet' },
  chesswebSocial:  { days: [1, 4], label: (d) => d, tooltip: '/chessweb-social — ~10 AM' },
  outreach:        { days: [0,1,2,3,4,5,6], label: () => 'Daily 9 AM', tooltip: 'Lichess coaches outreach — Daily 9:09 AM' },
  cwBlog:          { days: [2], label: (d) => d, tooltip: '/blog-internal — Tuesday ~10 AM' },
};

const scheduleProps = (key) => {
  const s = SCHEDULES[key];
  const day = getNextDay(s.days);
  return { schedule: s.label(day), scheduleTooltip: s.tooltip };
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [bookingStats, setBookingStats] = useState({ today: 0, thisWeek: 0, thisMonth: 0, weekGoal: 5, nextBooking: null });
  const [carouselCount, setCarouselCount] = useState(0);
  const defaultTypeCounts = { tweets: { total: 0, unreviewed: 0 }, quotes: { total: 0, unreviewed: 0 }, widgetTweets: { total: 0, unreviewed: 0 } };
  const [icCarousel, setIcCarousel] = useState(defaultTypeCounts);
  const [tcCarousel, setTcCarousel] = useState(defaultTypeCounts);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [cwhSocial, setCwhSocial] = useState({ postsThisWeek: 0 });
  const [cwhQueueCount, setCwhQueueCount] = useState(0);
  const [videoStats, setVideoStats] = useState({ total: 0, queued: 0, voiceReady: 0, rendered: 0, published: 0 });
  const [widgetStats, setWidgetStats] = useState({ pending: 0, approved: 0 });
  const [unreviewedCarousel, setUnreviewedCarousel] = useState(0);
  const [dailyActions, setDailyActions] = useState({});
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const todayKey = new Date().toISOString().split('T')[0];
      const results = await Promise.allSettled([
        getDailySummaryStats(),
        getFollowUpQueue(),
        getBookingStats(),
        getCarouselQueueCount(),
        getChessWebSocialStats(),
        getVideoQueueStats(),
        getSubmissionStats(),
        getUnreviewedCarouselCount(),
        getDailyActions(todayKey),
        getCarouselCountsByTypeAndAccount('indiechess'),
        getCarouselCountsByTypeAndAccount('trendingchess'),
        getChessWebSocialQueueCount(),
        getAutoRejectedCarouselCount()
      ]);

      const val = (i, fallback) => results[i].status === 'fulfilled' ? results[i].value : fallback;

      // Log any failures for debugging
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['dailySummary', 'followUp', 'bookings', 'carousel', 'cwhSocial', 'videoQueue', 'submissions', 'unreviewedCarousel', 'dailyActions', 'icCarousel', 'tcCarousel', 'cwhQueue', 'rejected'];
          console.warn(`AdminPage: ${names[i]} failed:`, r.reason?.message || r.reason);
        }
      });

      setStats(val(0, null));
      setFollowUpCount(val(1, []).length);
      setBookingStats(val(2, { today: 0, thisWeek: 0, thisMonth: 0, weekGoal: 5, nextBooking: null }));
      setCarouselCount(val(3, 0));
      setCwhSocial(val(4, { postsThisWeek: 0 }));
      setVideoStats(val(5, { total: 0, queued: 0, voiceReady: 0, rendered: 0, published: 0 }));
      setWidgetStats(val(6, { pending: 0, approved: 0 }));
      setUnreviewedCarousel(val(7, 0));
      setDailyActions(val(8, null) || {});
      setIcCarousel(val(9, defaultTypeCounts));
      setTcCarousel(val(10, defaultTypeCounts));
      setCwhQueueCount(val(11, 0));
      setRejectedCount(val(12, 0));
    } catch (err) {
      console.error('AdminPage load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const videoInProgress = videoStats.queued + videoStats.voiceReady;

  // Carousel row helper: three states — needs review (yellow), scheduled (green), empty (orange)
  const carouselRow = (counts, label, route, extraGroupProps = {}, { postSchedule, fillSchedule } = {}) => {
    const { unreviewed, total } = counts;
    const scheduled = total - unreviewed;
    if (unreviewed > 0) {
      return {
        ...extraGroupProps,
        faIcon: faEye, iconColor: 'text-yellow-400',
        label,
        value: `${unreviewed} to review${scheduled > 0 ? ` · ${scheduled} scheduled` : ''}`,
        action: 'Review →', route,
      };
    }
    if (total > 0) {
      return {
        ...extraGroupProps,
        faIcon: faCircleCheck, iconColor: 'text-green-400',
        label,
        value: `${total} scheduled`,
        route,
        ...(postSchedule ? scheduleProps(postSchedule) : {}),
      };
    }
    return {
      ...extraGroupProps,
      faIcon: faTriangleExclamation, iconColor: 'text-orange-400',
      label,
      value: 'Queue empty',
      route,
      ...(fillSchedule ? scheduleProps(fillSchedule) : {}),
    };
  };

  const handleDailyActionToggle = async (field, value) => {
    const todayKey = new Date().toISOString().split('T')[0];
    setDailyActions(prev => ({ ...prev, [field]: value }));
    try {
      await updateDailyAction(todayKey, field, value);
    } catch (err) {
      console.error('Failed to update daily action:', err);
      setDailyActions(prev => ({ ...prev, [field]: !value }));
    }
  };

  return (
    <AdminAuth>
      <div className="max-w-[1800px] mx-auto px-3 space-y-4">

        {/* Action Alerts Bar */}
        {!loading && (
          <ActionBar
            blogDrafts={stats?.blogDrafts || 0}
            emailsDraft={stats?.emailsDraft || 0}
            unreviewedCarousel={unreviewedCarousel}
            widgetsPending={widgetStats.pending}
            dailyActions={dailyActions}
            onDailyActionToggle={handleDailyActionToggle}
          />
        )}



        {/* Engines: 3-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">

        {/* Engine: IndieChess */}
        <EngineSection
          name="IndieChess"
          color="gold"
          defaultExpanded={true}
          progressBars={[]}
          rows={[
            { group: 'YouTube @IndieChess', groupIcon: faYoutube, groupIconColor: 'text-red-500', faIcon: faYoutube, iconColor: 'text-red-500', label: 'Video Pipeline', value: `${videoInProgress} in progress · ${videoStats.rendered} ready`, action: 'Review →', route: '/admin/content?tab=video' },
            carouselRow(icCarousel.tweets, 'Best Chess Tweets', '/admin/instagram?tab=tweets', { group: 'Instagram @IndieChess', groupIcon: faInstagram, groupIconColor: 'text-pink-400' }, { postSchedule: 'tweetPostIC', fillSchedule: 'tweetFetch' }),
            carouselRow(icCarousel.quotes, 'Quote Carousel', '/admin/instagram?tab=quotes', { group: 'Instagram @IndieChess' }, { postSchedule: 'quotePostIC', fillSchedule: 'quoteFetch' }),
          ]}
          channels={[
            { icon: faXTwitter, iconColor: 'text-[#9CA3AF]', handle: '@IndieChess', url: 'https://x.com/IndieChess', description: 'Personal distribution — chess + AI + tech' },
            { icon: faInstagram, iconColor: 'text-pink-400', handle: '@IndieChess', url: 'https://instagram.com/indiechess', description: 'Short-form content' },
            { icon: faYoutube, iconColor: 'text-red-500', handle: '@IndieChess', url: 'https://youtube.com/@IndieChess', description: 'Tutorials, chess tech education' },
            { icon: faTiktok, iconColor: 'text-[#D1D5DB]', handle: '@IndieChess', url: 'https://tiktok.com/@IndieChess', description: 'Short-form content' },
            { icon: faLinkedin, iconColor: 'text-blue-400', handle: 'Christopher P.', url: 'https://linkedin.com/in/christopherpoloniato', description: 'Chess Coach Content on LinkedIn' },
            { icon: faEnvelope, iconColor: 'text-yellow-400', handle: 'christopher@indiechess.com', url: 'mailto:christopher@indiechess.com', description: 'Personal email' },
          ]}
          skills={[
            { command: '/video-pipeline', description: 'Full pipeline: voiceover \u2192 avatar \u2192 B-roll mix' },
            { command: '/broll-mix', description: 'Mix avatar video with B-roll overlays and effects' },
            { command: '/chess-video', description: 'Generate chess game animation from PGN' },
            { command: '/monitor-calendar', description: 'Analyze bookings, track conversions' },
          ]}
        />

        {/* Engine: ChessWeb.site */}
        <EngineSection
          name="ChessWeb.site"
          color="green"
          defaultExpanded={true}
          progressBars={[]}
          rows={[
            { group: 'Outreach', groupIcon: faEnvelope, groupIconColor: 'text-yellow-400', faIcon: faEnvelope, iconColor: 'text-yellow-400', label: 'Cold Outreach', ...((stats?.emailsDraft || 0) > 0 ? { value: `${stats.emailsDraft} drafts`, action: 'Review →', route: '/admin/outreach' } : { value: 'No drafts pending', route: '/admin/outreach', ...scheduleProps('outreach') }) },
            { group: 'Blog', groupIcon: faBlog, groupIconColor: 'text-[#8AB563]', faIcon: faBlog, iconColor: 'text-[#8AB563]', label: 'ChessWeb.site Blog', value: 'Edit from ChessWeb.site', action: 'Admin →', href: 'http://localhost:3000/dashboard/website/blogs' },
            { group: 'Twitter @ChessWebsite', groupIcon: faXTwitter, groupIconColor: 'text-[#9CA3AF]', faIcon: faXTwitter, iconColor: 'text-[#9CA3AF]', label: 'Product Tweets', value: cwhQueueCount > 0 ? `${cwhQueueCount} queued` : '/chessweb-social · Mon + Thu', ...(cwhQueueCount > 0 ? { action: 'Review →', route: '/admin/instagram?tab=chessweb' } : scheduleProps('chesswebSocial')) },
            { group: 'Instagram @chesswebsite', groupIcon: faInstagram, groupIconColor: 'text-pink-400', faIcon: faInstagram, iconColor: 'text-pink-400', label: 'Product Carousel', value: cwhQueueCount > 0 ? `${cwhQueueCount} queued` : 'Same as tweets, carousel format', ...(cwhQueueCount > 0 ? { action: 'Review →', route: '/admin/instagram?tab=chessweb' } : scheduleProps('chesswebSocial')) },
          ]}
          channels={[
            { icon: faXTwitter, iconColor: 'text-[#9CA3AF]', handle: '@ChessWebsite', url: 'https://x.com/ChessWebsite', description: 'ChessWeb.site product account' },
            { icon: faInstagram, iconColor: 'text-pink-400', handle: '@chesswebsite', url: 'https://instagram.com/chesswebsite', description: 'ChessWeb.site on Instagram' },
            { icon: faBlog, iconColor: 'text-[#8AB563]', handle: 'ChessWeb.site Blog', url: 'https://chessweb.site/guide', description: 'Product-focused, use-case-driven blog' },
            { icon: faEnvelope, iconColor: 'text-yellow-400', handle: 'Cold Outreach', url: '#', description: 'Sales emails \u2014 sell websites & academies' },
          ]}
          skills={[
            { command: '/create-profile', description: 'Generate player profile websites via AI' },
            { command: '/add-custom-domain', description: 'Add custom domain for a player profile' },
            { command: '/outreach-research', description: 'Find leads, draft cold emails' },
            { command: '/monitor-inbox', description: 'Review inbox, categorize, match to leads' },
            { command: '/inbox-reply', description: 'Draft replies to inbox emails' },
          ]}
        />

        {/* Engine: TrendingChess */}
        <EngineSection
          name="TrendingChess"
          color="blue"
          defaultExpanded={true}
          progressBars={[]}
          rows={[
            { group: 'Discovery', groupIcon: faMagnifyingGlass, groupIconColor: 'text-blue-300', faIcon: faMagnifyingGlass, iconColor: 'text-blue-300', label: 'Widget Index', ...(widgetStats.pending > 0 ? { value: `${widgetStats.pending} pending review`, action: 'Review →', route: '/admin/widgets?tab=submissions' } : { value: 'All reviewed', ...scheduleProps('widgetDiscovery') }) },
            { group: 'Blog: TrendingChess.com', groupIcon: faBlog, groupIconColor: 'text-blue-400', faIcon: faBlog, iconColor: 'text-blue-400', label: 'Ecosystem Blog', value: `${stats?.contentDraft || 0} drafts`, action: 'Review →', route: '/admin/content' },
            carouselRow(tcCarousel.widgetTweets, 'Widget Tweets', '/admin/instagram?tab=widget_tweets', { group: 'Twitter @TrendingChess', groupIcon: faXTwitter, groupIconColor: 'text-[#9CA3AF]' }, { postSchedule: 'tweetPostTC', fillSchedule: 'widgetDiscovery' }),
            { group: 'Twitter @TrendingChess', faIcon: faXTwitter, iconColor: 'text-[#9CA3AF]', label: 'Blog Thread Tweets', value: `${stats?.tweetsPostedToday || 0} posted today`, action: 'Review →', route: '/admin/tweets' },
            carouselRow(tcCarousel.tweets, 'Best Chess Tweets', '/admin/instagram?tab=tweets', { group: 'Instagram @chesstrending', groupIcon: faInstagram, groupIconColor: 'text-pink-400' }, { postSchedule: 'tweetPostTC', fillSchedule: 'tweetFetch' }),
            carouselRow(tcCarousel.quotes, 'Quote Carousel', '/admin/instagram?tab=quotes', { group: 'Instagram @chesstrending' }, { postSchedule: 'quotePostTC', fillSchedule: 'quoteFetch' }),
            carouselRow(tcCarousel.widgetTweets, 'Widget Tweets', '/admin/instagram?tab=widget_tweets', { group: 'Instagram @chesstrending' }, { postSchedule: 'tweetPostTC', fillSchedule: 'widgetDiscovery' }),
          ]}
          channels={[
            { icon: faXTwitter, iconColor: 'text-[#9CA3AF]', handle: '@TrendingChess', url: 'https://x.com/TrendingChess', description: 'AI-augmented chess news' },
            { icon: faInstagram, iconColor: 'text-pink-400', handle: '@chesstrending', url: 'https://instagram.com/chesstrending', description: 'AI-driven chess content' },
            { icon: faBlog, iconColor: 'text-blue-400', handle: 'TrendingChess.com Blog', url: 'https://trendingchess.com/blog', description: 'AI-generated chess/tech articles' },
            { icon: faGlobe, iconColor: 'text-blue-300', handle: 'TrendingChess.com', url: 'https://trendingchess.com', description: 'Chess ecosystem index \u2014 every entity gets a landing page' },
          ]}
          skills={[
            { command: '/widget-spotlight', description: 'Research app, enrich detail page, blog + social draft' },
            { command: '/discover-listings', description: 'Search web for chess apps/creators, submit to directory' },
            { command: '/content-research', description: 'Research chess topic \u2192 blog draft \u2192 hero image' },
          ]}
        />

        </div>{/* end engines grid */}

        {/* Content Schedule */}
        <ContentSchedule rejectedCount={rejectedCount} />

        {/* Calendar Embed */}
        <CalendarEmbed onFullView={() => navigate('/admin/calendar')} bookingStats={bookingStats} />

        {/* Daily Progress */}
        <DailyProgress
          stats={stats}
          tweetsPostedToday={stats?.tweetsPostedToday || 0}
          dailyActions={dailyActions}
          onDailyActionToggle={handleDailyActionToggle}
        />
      </div>
    </AdminAuth>
  );
};

const SCHEDULE_DAYS = [
  {
    day: 'Mon',
    items: [
      { label: 'CW Social', engine: 'green', description: '@ChessWebsite X + IG drafts' },
    ]
  },
  {
    day: 'Tue',
    items: [
      { label: 'CW Blog', engine: 'green', description: 'SEO blog for ChessWeb.site' },
      { label: 'Tweet Fetch', engine: 'blue', description: 'Desktop + Cloud Fn auto-fetch' },
    ]
  },
  {
    day: 'Wed',
    items: [
      { label: 'Quote Fetch', engine: 'gold', description: 'Quotes for carousel queue' },
      { label: 'Post Quotes', engine: 'blue', description: '@TrendingChess IG carousel' },
      { label: 'Post Tweets', engine: 'gold', description: '@IndieChess IG carousel' },
    ]
  },
  {
    day: 'Thu',
    items: [
      { label: 'CW Social', engine: 'green', description: '@ChessWebsite X + IG drafts' },
      { label: 'Tweet Fetch', engine: 'blue', description: 'Cloud Fn auto-fetch' },
    ]
  },
  {
    day: 'Fri',
    items: []
  },
  {
    day: 'Sat',
    items: [
      { label: 'Tweet Fetch', engine: 'blue', description: 'Desktop + Cloud Fn auto-fetch' },
    ]
  },
  {
    day: 'Sun',
    items: [
      { label: 'Post Tweets', engine: 'blue', description: '@TrendingChess IG carousel' },
      { label: 'Post Quotes', engine: 'gold', description: '@IndieChess IG carousel' },
    ]
  },
];

const DAILY_ITEMS = [
  { label: 'Content Research', engine: 'blue', description: '3 topics/day' },
  { label: 'Widget Discovery', engine: 'blue', description: '10 new entities/day' },
  { label: 'Lichess Coaches', engine: 'green', description: '10 coaches/day' },
];

const ENGINE_COLORS = {
  gold: { dot: 'bg-[#FFD700]', text: 'text-[#FFD700]', bg: 'bg-[#FFD700]/10', border: 'border-[#FFD700]/20' },
  green: { dot: 'bg-[#8AB563]', text: 'text-[#8AB563]', bg: 'bg-[#8AB563]/10', border: 'border-[#8AB563]/20' },
  blue: { dot: 'bg-[#60A5FA]', text: 'text-[#60A5FA]', bg: 'bg-[#60A5FA]/10', border: 'border-[#60A5FA]/20' },
};

const ContentSchedule = ({ rejectedCount = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const todayIdx = (new Date().getDay() + 6) % 7; // Mon=0

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCalendarDays} className="text-[#9CA3AF] text-xs" />
          <h3 className="text-sm font-semibold text-[#F9FAFB]">Content Schedule</h3>
          <span className="text-[10px] text-[#6B7280] ml-1">Weekly output calendar</span>
          {rejectedCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
              {rejectedCount} auto-rejected
            </span>
          )}
        </div>
        <span className={`text-[#9CA3AF] text-xs transition-transform ${expanded ? 'rotate-180' : ''}`}>&#9660;</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Daily recurring */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium w-12 shrink-0">Daily</span>
            {DAILY_ITEMS.map((item) => {
              const c = ENGINE_COLORS[item.engine];
              return (
                <span key={item.label} className={`text-[10px] px-2 py-1 rounded-md ${c.bg} ${c.text} ${c.border} border`} title={item.description}>
                  {item.label}
                </span>
              );
            })}
          </div>

          <div className="border-t border-gray-700" />

          {/* Weekly grid */}
          <div className="space-y-1.5">
            {SCHEDULE_DAYS.map((day, idx) => {
              const isToday = idx === todayIdx;
              return (
                <div key={day.day} className={`flex items-center gap-2 ${isToday ? 'bg-[#1F2937] rounded-lg px-2 py-1.5 -mx-2' : 'py-0.5'}`}>
                  <span className={`text-[10px] uppercase tracking-wide font-medium w-12 shrink-0 ${isToday ? 'text-[#FFD700]' : 'text-[#6B7280]'}`}>
                    {isToday ? `${day.day} *` : day.day}
                  </span>
                  {day.items.length === 0 ? (
                    <span className="text-[10px] text-[#374151] italic">No scheduled outputs</span>
                  ) : (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {day.items.map((item) => {
                        const c = ENGINE_COLORS[item.engine];
                        return (
                          <span key={item.label} className={`text-[10px] px-2 py-1 rounded-md ${c.bg} ${c.text} ${c.border} border`} title={item.description}>
                            {item.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 pt-1 border-t border-gray-700">
            {Object.entries(ENGINE_COLORS).map(([name, c]) => (
              <div key={name} className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                <span className="text-[10px] text-[#6B7280] capitalize">{name === 'gold' ? 'IndieChess' : name === 'green' ? 'ChessWeb' : 'TrendingChess'}</span>
              </div>
            ))}
            <span className="text-[10px] text-[#4B5563] ml-auto">All outputs land as Post-Bridge drafts</span>
          </div>
        </div>
      )}
    </div>
  );
};

const CalendarEmbed = ({ onFullView, bookingStats }) => {
  const [expanded, setExpanded] = useState(false);
  const { today, thisWeek, thisMonth, weekGoal, nextBooking } = bookingStats;
  const weekProgress = Math.min((thisWeek / weekGoal) * 100, 100);
  const hitGoal = thisWeek >= weekGoal;

  const formatNextBooking = (booking) => {
    if (!booking) return null;
    const date = booking.startTime?.toDate?.() || new Date(booking.startTime);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const dateStr = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    return { dateStr, timeStr, name: booking.attendeeName || booking.summary || 'Upcoming Call' };
  };

  const nextCall = formatNextBooking(nextBooking);

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#374151]">
        <h3 className="text-sm font-semibold text-[#F9FAFB]">My Calendar</h3>
        <button
          onClick={onFullView}
          className="text-xs text-[#60A5FA] hover:text-[#93C5FD] transition-colors"
        >
          Full View &rarr;
        </button>
      </div>

      <div className="px-4 py-4 bg-gradient-to-r from-[#1F2937] to-[#111827]">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-[#111827]/70 rounded-xl p-4 border border-[#374151] text-center">
            <span className="text-xs uppercase tracking-wide text-[#9CA3AF] font-medium block mb-2">Today</span>
            <span className={`text-3xl font-bold ${today > 0 ? 'text-[#FFD700]' : 'text-[#4B5563]'}`}>{today}</span>
          </div>

          <div className="bg-[#111827]/70 rounded-xl p-4 border border-[#374151]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-[#9CA3AF] font-medium">This Week</span>
              {hitGoal && (
                <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full font-medium">
                  Goal!
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className={`text-3xl font-bold ${hitGoal ? 'text-green-400' : 'text-[#FFD700]'}`}>
                {thisWeek}
              </span>
              <span className="text-[#9CA3AF] text-sm">/ {weekGoal}</span>
            </div>
            <div className="h-1.5 bg-[#374151] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${hitGoal ? 'bg-gradient-to-r from-green-500 to-green-400' : 'bg-gradient-to-r from-[#C8A800] to-[#FFD700]'}`}
                style={{ width: `${weekProgress}%` }}
              />
            </div>
          </div>

          <div className="bg-[#111827]/70 rounded-xl p-4 border border-[#374151] text-center">
            <span className="text-xs uppercase tracking-wide text-[#9CA3AF] font-medium block mb-2">MTD</span>
            <span className="text-3xl font-bold text-[#60A5FA]">{thisMonth}</span>
          </div>
        </div>

        {nextCall ? (
          <div className="bg-[#111827]/70 rounded-xl p-3 border border-[#374151] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
                <span className="text-[#FFD700] text-lg">&#128197;</span>
              </div>
              <div>
                <p className="text-sm font-medium text-[#F9FAFB]">{nextCall.name}</p>
                <p className="text-xs text-[#9CA3AF]">Next call</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-[#FFD700]">{nextCall.dateStr}</p>
              <p className="text-xs text-[#9CA3AF]">{nextCall.timeStr}</p>
            </div>
          </div>
        ) : (
          <div className="bg-[#111827]/70 rounded-xl p-3 border border-[#374151] text-center">
            <p className="text-sm text-[#9CA3AF]">No upcoming calls scheduled</p>
          </div>
        )}
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 border-t border-[#374151] bg-[#1F2937]/50 hover:bg-[#1F2937] transition-colors flex items-center justify-center gap-2 text-xs text-[#9CA3AF] hover:text-[#D1D5DB]"
      >
        <span>{expanded ? 'Hide Calendar' : 'Show Calendar'}</span>
        <span className={`transition-transform ${expanded ? 'rotate-180' : ''}`}>&#9660;</span>
      </button>

      {expanded && (
        <iframe
          src="https://calendar.google.com/calendar/embed?src=9e96ccb58a5f855a924f8ce83aafa3d1e2426d80367f622e87c5c67c9a243e26%40group.calendar.google.com&ctz=Africa%2FJohannesburg&showTitle=0&showNav=1&showPrint=0&showTabs=0&showCalendars=0"
          style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg)' }}
          width="100%"
          height="350"
          frameBorder="0"
          scrolling="no"
          title="My Calendar"
        />
      )}
    </div>
  );
};

export default AdminPage;
